import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bar, BarChart, CartesianGrid, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { FaDollarSign } from "react-icons/fa";

const GroupedPayments = () => {
  const [payments, setPayments] = useState({});
  const [paymentBalance, setPaymentBalance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    axios
      .get(`${apiUrl}/payment/getGroupedPayments`, {
        headers: {
          loginToken: localStorage.getItem("loginToken"),
        },
      })
      .then((response) => {
        setPayments(response.data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });

    // Fetch all payments with balance to calculate due per year
    axios
      .get(`${apiUrl}/payment/getAllPaymentsWithBalance`, {
        headers: {
          loginToken: localStorage.getItem("loginToken"),
        },
      })
      .then((response) => {
        console.log('All Payments With Balance:', response.data);
        setPaymentBalance(response.data);
      })
      .catch((error) => {
        console.log("Error fetching payment balance:", error);
      });
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-80"><p className="text-gray-500">Loading...</p></div>;
  }

  if (error) {
    return <div className="flex items-center justify-center h-80"><p className="text-red-500">Error: {error}</p></div>;
  }

  const getChartData = (payments, paymentBalance) => {
    // Get collected payments by year
    const collectedByYear = Object.keys(payments)
      .sort((a, b) => parseInt(b) - parseInt(a))
      .reduce((acc, year) => {
        const total = Object.values(payments[year].months).reduce(
          (sum, { total }) => sum + parseFloat(total),
          0
        );
        acc[year] = { collected: total };
        return acc;
      }, {});

    // Get due payments by year from getAllPaymentsWithBalance data
    // The API already calculates balance = initialAmount - totalPayments
    const dueByYear = paymentBalance
      .filter(item => item.year)
      .reduce((acc, item) => {
        const year = item.year;
        const due = parseFloat(item.balance) || 0;
        
        if (!acc[year]) {
          acc[year] = { due: 0 };
        }
        acc[year].due += due;
        return acc;
      }, {});

    console.log('Collected by Year:', collectedByYear);
    console.log('Due by Year (from getAllPaymentsWithBalance):', dueByYear);

    // Merge data
    const years = new Set([...Object.keys(collectedByYear), ...Object.keys(dueByYear)]);
    const data = Array.from(years)
      .sort((a, b) => parseInt(b) - parseInt(a))
      .map(year => ({
        name: year,
        "Collected": collectedByYear[year]?.collected || 0,
        "Due": dueByYear[year]?.due || 0,
      }));

    console.log('Final Chart Data:', data);
    return data;
  };

  const chartData = getChartData(payments, paymentBalance);

  const CustomBar = (props) => {
    const { x, y, width, height, fill } = props;
    return (
      <Rectangle
        x={x}
        y={y - 10}
        width={width}
        height={height}
        fill={fill}
        radius={[12, 12, 0, 0]}
      />
    );
  };

  const CustomLabel = (props) => {
    const { x, y, width, value } = props;
    const formattedValue = `RM ${Math.round(value)}`;
    return (
      <text 
        x={x + width / 2} 
        y={y - 5} 
        fill="#1f2937" 
        textAnchor="middle" 
        fontSize={11}
        fontWeight="bold"
      >
        {formattedValue}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-gray-700 font-semibold">Year: {payload[0].payload.name}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="font-bold">
              {entry.name}: RM {Math.round(entry.value).toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="w-full flex flex-col gap-2 pt-2 mb-0">
      <div className="flex items-center gap-2">
        <FaDollarSign className="text-green-600 text-xl" />
        <h2 className="text-xl font-bold text-gray-800">Payment Summary</h2>
      </div>
      <p className="text-gray-500 text-sm mb-2">Collected vs Due payments by year</p>
      <ResponsiveContainer width='100%' height={260}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 12, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis 
            tick={{ fontSize: 12, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="square"
          />
          <Bar 
            dataKey="Collected" 
            fill="#10b981" 
            label={<CustomLabel />}
            shape={CustomBar}
          />
          <Bar 
            dataKey="Due" 
            fill="#ef4444" 
            label={<CustomLabel />}
            shape={CustomBar}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default GroupedPayments;
